import dayjs, { Dayjs } from 'dayjs';
import { MeetingRoom } from '../types/models';

/**
 * 회의실 사용 종료일(rooms.available_until) 관련 도우미.
 *
 * available_until 은 'YYYY-MM-DD' 문자열이며 "이 날짜까지 회의 가능"을 뜻한다.
 * 그 다음 날부터는 해당 회의실로 예약할 수 없다. 값이 없으면 제한이 없는 회의실이다.
 *
 * 비교는 Date 로 바꾸지 않고 'YYYY-MM-DD' 문자열끼리 한다.
 * 이 형식은 사전순 비교가 곧 날짜순 비교라, 시간대 때문에 하루 밀리는 사고가 없다.
 */

const toDateKey = (value: Dayjs | Date): string => dayjs(value).format('YYYY-MM-DD');

/** 사용 종료일이 설정된 회의실인지 */
export const hasClosureDate = (room: MeetingRoom): boolean =>
  typeof room.available_until === 'string' && room.available_until.length > 0;

/** 해당 날짜에 이 회의실을 예약할 수 있는지 */
export const canBookRoomOn = (room: MeetingRoom, date: Dayjs | Date): boolean =>
  !hasClosureDate(room) || toDateKey(date) <= (room.available_until as string);

/** 오늘 기준으로 사용이 이미 끝난 회의실인지 */
export const isRoomClosed = (room: MeetingRoom, today: Dayjs = dayjs()): boolean =>
  hasClosureDate(room) && (room.available_until as string) < toDateKey(today);

const WEEKDAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 안내 문구용. '10월 20일(화)'
 * 요일은 dayjs 로케일 설정에 기대지 않고 직접 붙인다.
 */
export const formatClosureDate = (room: MeetingRoom): string => {
  if (!hasClosureDate(room)) return '';
  const date = dayjs(room.available_until as string);
  return `${date.format('M월 D일')}(${WEEKDAY_NAMES[date.day()]})`;
};

/**
 * 회의실 목록 표시용. '~10/20까지 예약 가능'
 * 날짜만 적어 두면 그게 시작인지 끝인지 헷갈려서 뜻을 풀어 쓴다.
 * 문구는 안내 팝업과 맞춘다.
 */
export const formatClosureBadge = (room: MeetingRoom): string =>
  hasClosureDate(room)
    ? `~${dayjs(room.available_until as string).format('M/D')}까지 예약 가능`
    : '';

/**
 * 남은 일수 안내. '오늘까지' / '내일까지' / 'D-11'
 * 종료일이 지난 회의실에는 쓰지 않는다.
 */
export const formatDaysLeft = (room: MeetingRoom, today: Dayjs = dayjs()): string => {
  if (!hasClosureDate(room)) return '';
  const daysLeft = dayjs(room.available_until as string).diff(dayjs(toDateKey(today)), 'day');
  if (daysLeft <= 0) return '오늘까지';
  if (daysLeft === 1) return '내일까지';
  return `D-${daysLeft}`;
};

/**
 * 아직 안내할 가치가 있는 회의실. 사용 종료일이 남아 있는 것만 골라 날짜 순으로 준다.
 * 종료일이 지난 회의실은 빠지므로, 다 지나면 안내 자체가 사라진다.
 */
/**
 * 예약이 끝난 뒤에도 이 기간 동안은 안내에 남겨 둔다.
 * 막 끝난 직후가 "그 방 어디 갔지" 로 가장 헷갈리는 때라, 그때 설명이 사라지면 안 된다.
 */
const CLOSED_NOTICE_GRACE_DAYS = 14;

/** 종료일로부터 며칠이 지났는지. 아직 안 지났으면 음수. */
const daysSinceClosure = (room: MeetingRoom, today: Dayjs): number =>
  dayjs(toDateKey(today)).diff(dayjs(room.available_until as string), 'day');

export const getClosingRooms = (rooms: MeetingRoom[], today: Dayjs = dayjs()): MeetingRoom[] => {
  const withClosureDate = rooms.filter(
    room => hasClosureDate(room) && daysSinceClosure(room, today) <= CLOSED_NOTICE_GRACE_DAYS
  );

  // 기간이 걸린 회의실이 모두 끝났다면 더 알릴 것이 없다.
  // 끝난 회의실은 화면에서 빠지므로, 안내만 남으면 없는 방 이야기를 계속 하는 셈이다.
  const hasRoomStillOpen = withClosureDate.some(room => !isRoomClosed(room, today));
  if (!hasRoomStillOpen) return [];

  return withClosureDate.sort((a, b) =>
    (a.available_until as string).localeCompare(b.available_until as string)
  );
};

/**
 * 안내 내용을 나타내는 문자열.
 *
 * '다시 보지 않기'를 이 값으로 기억해 두면, 같은 내용은 다시 띄우지 않으면서도
 * 날짜가 바뀌거나 회의실이 추가되면 값이 달라져 다시 띄울 수 있다.
 */
export const buildClosureNoticeSignature = (rooms: MeetingRoom[]): string =>
  rooms.map(room => `${room.id}:${room.available_until}`).join('|');
